import { useState } from 'react'

import { useSesion } from '@/modules/auth/useSesion'
import { useFotosDelTrabajo } from './useFotosDelTrabajo'
import { LlegadaYSalida } from '@/modules/ejecucion/LlegadaYSalida'
import type { OrdenServicioConRelaciones } from '@/modules/ordenes-servicio/tipos'
import { useColaboradoresActivos } from '@/modules/programacion/useHerramientas'
import { useReasignarTecnico } from '@/modules/programacion/useProgramar'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'
import type { EstadoOrdenTrabajo } from '@/lib/supabase/tipos-bd'
import { ESTADOS_ORDEN_TRABAJO } from '@/shared/dominio/estados'
import { fechaHora } from '@/shared/utilidades/formato'

import { AjustarHoras } from './AjustarHoras'
import { DestinoDeLasHerramientas } from './DestinoDeLasHerramientas'
import { PasarAPrecierre } from './PasarAPrecierre'
import { SobrantesDeLaOrden } from '@/modules/sobrantes/SobrantesDeLaOrden'
import {
  useCerrarTrabajo,
  useDevolverAlTecnico,
  useDecidirMasTiempo,
  useRegistrarFirma,
  useSolicitarMasTiempo,
} from './usePrecierre'
import estilos from './TarjetaPrecierre.module.css'

type Props = { orden: OrdenServicioConRelaciones }

/**
 * Compuerta 2 · Precierre.
 *
 * La orden se cierra sola al acabarse su programación. Aquí el colaborador
 * pide más tiempo explicando por qué, y el Gerente operativo acepta o niega.
 * Las dos decisiones quedan en la bitácora con nombre y fecha.
 */
export function TarjetaPrecierre({ orden }: Props) {
  const { puede } = useSesion()
  const trabajo = orden.orden_trabajo

  const solicitar = useSolicitarMasTiempo()
  const decidir = useDecidirMasTiempo()
  const cerrar = useCerrarTrabajo()
  const devolver = useDevolverAlTecnico()
  const firmar = useRegistrarFirma()
  const reasignar = useReasignarTecnico()
  const { colaboradores } = useColaboradoresActivos()
  const { data: fotosDelCierre = [] } = useFotosDelTrabajo(orden.orden_trabajo?.id ?? null)

  const [horas, setHoras] = useState('4')
  // Las que se le van a DAR. Arranca con lo que pidió —es lo que se aprueba
  // casi siempre— y se puede cambiar antes de decidir. Va aquí arriba con el
  // resto: debajo del `return` de «sin trabajo» React no admite estados.
  const [horasADar, setHorasADar] = useState(
    String(orden.orden_trabajo?.horas_adicionales_solicitadas ?? ''),
  )
  const [justificacion, setJustificacion] = useState('')
  const [notas, setNotas] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [cerrando, setCerrando] = useState(false)
  const [motivoCierre, setMotivoCierre] = useState('')
  const [devolviendo, setDevolviendo] = useState(false)
  const [motivoDevolucion, setMotivoDevolucion] = useState('')
  const [cambiandoTecnico, setCambiandoTecnico] = useState(false)
  const [nuevoTecnico, setNuevoTecnico] = useState('')

  if (!trabajo) return null

  const enPrecierre = trabajo.estado === 'en_precierre'
  const pidioMasTiempo = trabajo.solicita_mas_tiempo

  // Quién decide la compuerta: quien tenga el PERMISO, no quien tenga el rol.
  // Antes se decidía por rol y dejaba fuera a Proyectos, que desde la 0065
  // tiene los mismos permisos que el coordinador. Gerencia entra por la 0083,
  // que le da ordenes_trabajo.aprobar —el trabajo no puede quedarse quieto si
  // el coordinador no está—. Así la pantalla y la base dicen lo mismo.
  const puedeDecidir = puede('ordenes_trabajo', 'aprobar')

  const horasHechas = Number(trabajo.horas_ejecutadas ?? 0) + Number(trabajo.horas_extra ?? 0)

  async function devolverAlTecnico() {
    if (!trabajo) return
    setError(null)

    try {
      await devolver.mutateAsync({
        ordenTrabajoId: trabajo.id,
        motivo: motivoDevolucion.trim(),
      })
      setDevolviendo(false)
      setMotivoDevolucion('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo devolver la orden.')
    }
  }

  async function terminar() {
    if (!trabajo) return
    setError(null)

    try {
      await cerrar.mutateAsync({
        ordenTrabajoId: trabajo.id,
        notas: motivoCierre.trim() || null,
      })
      setCerrando(false)
      setMotivoCierre('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo cerrar.')
    }
  }

  async function confirmarReasignacion() {
    if (!trabajo || !nuevoTecnico) return
    setError(null)
    try {
      await reasignar.mutateAsync({ ordenTrabajoId: trabajo.id, colaboradorId: nuevoTecnico })
      setCambiandoTecnico(false)
      setNuevoTecnico('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo reasignar.')
    }
  }

  async function registrarFirma() {
    if (!trabajo) return
    setError(null)

    try {
      await firmar.mutateAsync({ ordenTrabajoId: trabajo.id, notas: null })
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo registrar la firma.')
    }
  }

  async function pedir() {
    if (!trabajo) return
    setError(null)

    try {
      await solicitar.mutateAsync({
        ordenTrabajoId: trabajo.id,
        horas: Number(horas),
        justificacion,
      })
      setJustificacion('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible pedir más tiempo.')
    }
  }

  async function resolver(aprobar: boolean) {
    if (!trabajo) return
    setError(null)

    try {
      /*
       * SE DAN LAS HORAS QUE SE DECIDEN, NO LAS QUE SE PIDEN.
       *
       * Esto mandaba `horasAprobadas: null` siempre, y la base entiende el
       * nulo como «las que pidió el técnico». O sea que quien decidía no
       * decidía nada: solo podía dar el sí o el no a la cifra ajena. Si
       * pedía diez horas y le correspondían cuatro, no había dónde
       * escribirlo —había que negarlo entero y pedirle que volviera a
       * pedirlas bien—.
       *
       * Ahora el campo arranca con lo que pidió, que es lo que se aprueba la
       * mayoría de las veces, y se puede cambiar antes de dar.
       */
      const pedidas = Number(trabajo.horas_adicionales_solicitadas ?? 0)
      const aDar = Number(horasADar)

      await decidir.mutateAsync({
        ordenTrabajoId: trabajo.id,
        aprobar,
        horasAprobadas:
          aprobar && Number.isFinite(aDar) && aDar > 0 && aDar !== pedidas ? aDar : null,
        notas: notas.trim() || null,
      })
      setNotas('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible registrar la decisión.')
    }
  }

  return (
    <Tarjeta titulo="Orden de trabajo" subtitulo="Documento 3 · Lo que se programa y se ejecuta">
      <div className={estilos.resumen}>
        <span className={estilos.numero}>{trabajo.numero}</span>
        {/* Del catálogo, no armado a mano: reemplazar los guiones bajos daba
            «en ejecucion», que el CSS capitalizaba a «En Ejecucion» —sin
            tilde y con la E de más—. El catálogo ya tenía el texto bueno. */}
        <span
          className={`${estilos.estado} ${estilos[trabajo.estado] ?? ''}`}
          title={ESTADOS_ORDEN_TRABAJO[trabajo.estado as EstadoOrdenTrabajo]?.explicacion}
        >
          {ESTADOS_ORDEN_TRABAJO[trabajo.estado as EstadoOrdenTrabajo]?.etiqueta ?? trabajo.estado}
        </span>
      </div>

      <div className={estilos.horas}>
        <Cifra rotulo="Asignadas" valor={trabajo.horas_asignadas} />
        <Cifra rotulo="Ejecutadas" valor={trabajo.horas_ejecutadas} />
        <Cifra rotulo="Extra" valor={trabajo.horas_extra} />
      </div>

      {/* Cuándo está agendado: inicio y fin dan el marco del día. */}
      <div className={estilos.programacion}>
        <span className={estilos.programacionTecnico}>
          {trabajo.colaborador?.nombre ?? 'Sin técnico'}
        </span>
        {trabajo.fecha_inicio_programada && (
          <span className={estilos.programacionFecha}>
            {fechaHora(trabajo.fecha_inicio_programada)}
            {trabajo.fecha_fin_programada && <> → {fechaHora(trabajo.fecha_fin_programada)}</>}
          </span>
        )}
      </div>

      {/* Cambiar técnico cuando algo sale mal en camino o en el sitio. */}
      {puedeDecidir && ['programada', 'en_ejecucion'].includes(trabajo.estado) && (
        <div className={estilos.reasignar}>
          {cambiandoTecnico ? (
            <>
              <select
                className={estilos.selectorTecnico}
                value={nuevoTecnico}
                onChange={(e) => setNuevoTecnico(e.target.value)}
              >
                <option value="">Elegir técnico…</option>
                {colaboradores.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
              <Boton
                variante="principal"
                onClick={() => void confirmarReasignacion()}
                trabajando={reasignar.isPending}
                disabled={!nuevoTecnico}
              >
                Confirmar
              </Boton>
              <Boton
                variante="secundario"
                onClick={() => {
                  setCambiandoTecnico(false)
                  setNuevoTecnico('')
                }}
              >
                Cancelar
              </Boton>
            </>
          ) : (
            <Boton variante="secundario" onClick={() => setCambiandoTecnico(true)}>
              Cambiar técnico
            </Boton>
          )}
          {/* Cuando el técnico no puede pasarla él (0269). */}
          {!cambiandoTecnico && <PasarAPrecierre ordenTrabajoId={trabajo.id} />}
        </div>
      )}

      {/* La valvula de la regla estricta: lo que no cuenta el GPS pero si es
          trabajo, lo suma el Gerente operativo con su nombre. Y en la otra
          pestaña, correr el horario cuando la cita se movió. */}
      {puedeDecidir && (
        <AjustarHoras
          ordenTrabajoId={trabajo.id}
          horasActuales={Number(trabajo.horas_ejecutadas)}
          inicioProgramado={trabajo.fecha_inicio_programada}
          horasAsignadas={Number(trabajo.horas_asignadas)}
        />
      )}

      {/* El reloj de verdad: desde que el operario llega hasta que sale. */}
      <LlegadaYSalida
        ordenTrabajoId={trabajo.id}
        estado={trabajo.estado}
        solicitaInicio={trabajo.solicita_inicio_sin_ubicacion}
        inicioProgramado={trabajo.fecha_inicio_programada}
        finProgramado={trabajo.fecha_fin_programada}
        horasAsignadas={trabajo.horas_asignadas}
      />

      {/* ── LO QUE REPORTÓ EL TÉCNICO AL TERMINAR ───────────────────────
          Quien cierra le está pidiendo al cliente que firme conformidad, y
          hasta ahora lo hacía sin ver una sola foto ni leer lo que el técnico
          escribió. El relato va como lo dictó y, si la IA lo redactó para el
          acta, también esa versión: es la que el cliente va a leer. */}
      {(trabajo.resumen_trabajo || fotosDelCierre.length > 0) && (
        <div className={estilos.bloque}>
          <p className={estilos.tituloReporte}>Lo que reportó el técnico</p>

          {trabajo.resumen_trabajo && <p className={estilos.reporte}>{trabajo.resumen_trabajo}</p>}

          {trabajo.resumen_acta && trabajo.resumen_acta !== trabajo.resumen_trabajo && (
            <p className={estilos.reporteActa}>
              <span className={estilos.etiquetaActa}>Como saldrá en el acta</span>
              {trabajo.resumen_acta}
            </p>
          )}

          {fotosDelCierre.length > 0 && (
            <ul className={estilos.rejillaFotos}>
              {fotosDelCierre.map((f) =>
                f.url ? (
                  <li key={f.id}>
                    <a href={f.url} target="_blank" rel="noreferrer">
                      <img src={f.url} alt="Evidencia del trabajo" loading="lazy" />
                    </a>
                  </li>
                ) : null,
              )}
            </ul>
          )}
        </div>
      )}

      {error && (
        <div className={estilos.error}>
          <Aviso tipo="error">{error}</Aviso>
        </div>
      )}

      {/* ── Se le acabó el tiempo ──────────────────────────────────────── */}
      {/* PEDIR MÁS TIEMPO ES DEL TÉCNICO, NO DE LA OFICINA.
          Este formulario salía siempre, así que a gerencia le aparecía la
          pantalla del operario —«pida más tiempo explicando por qué»— cuando lo
          suyo es DECIDIR, no pedir. El técnico lo pide desde su propia tarjeta
          en el teléfono; quien decide no tiene nada que pedirse a sí mismo. Si
          la oficina quiere estirar el horario, eso es «Ajustar horas u
          horario», que está arriba. */}
      {enPrecierre && !pidioMasTiempo && !puedeDecidir && (
        <div className={estilos.bloque}>
          <Aviso tipo="alerta" titulo="Se acabó el tiempo programado">
            La orden se cerró sola. Si el trabajo no terminó, pida más tiempo explicando por qué.
          </Aviso>

          <div className={estilos.formulario}>
            <label className={estilos.campoHoras}>
              <span className={estilos.etiqueta}>Horas</span>
              <input
                type="number"
                min={0.5}
                step="0.5"
                value={horas}
                onChange={(e) => setHoras(e.target.value)}
                className={estilos.numeroCampo}
              />
            </label>

            <label className={estilos.campoTexto}>
              <span className={estilos.etiqueta}>Por qué necesita más tiempo</span>
              <textarea
                rows={3}
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                placeholder="Qué faltó y por qué"
                className={estilos.areaTexto}
              />
            </label>
          </div>

          <div className={estilos.acciones}>
            <Boton
              variante="principal"
              onClick={() => void pedir()}
              trabajando={solicitar.isPending}
              // Diez caracteres es lo que la base exige. Bloquear aquí evita
              // mandar algo que ya se sabe que va a rebotar.
              disabled={justificacion.trim().length < 10 || Number(horas) <= 0}
            >
              Pedir más tiempo
            </Boton>
          </div>
        </div>
      )}

      {/* ── Pidió y falta decidir ──────────────────────────────────────── */}
      {pidioMasTiempo && (
        <div className={estilos.bloque}>
          <div className={estilos.solicitud}>
            <p className={estilos.solicitudTitulo}>
              Pide {Number(trabajo.horas_adicionales_solicitadas)} horas más
            </p>
            <p className={estilos.solicitudTexto}>{trabajo.justificacion_mas_tiempo}</p>
          </div>

          {puedeDecidir ? (
            <>
              {/* CUÁNTAS SE DAN. Viene puesto lo que pidió —que es lo que se
                  aprueba casi siempre— y se puede bajar o subir antes de
                  decidir, sin tener que negar y pedirle que lo repita. */}
              <label className={estilos.campoHoras}>
                <span className={estilos.etiqueta}>Horas que se dan</span>
                <input
                  type="number"
                  min={0.5}
                  step="0.5"
                  value={horasADar}
                  onChange={(e) => setHorasADar(e.target.value)}
                  className={estilos.numeroCampo}
                />
              </label>

              <label className={estilos.campoTexto}>
                <span className={estilos.etiqueta}>
                  Notas de la decisión{' '}
                  <span className={estilos.obligatorio}>obligatorias para negar</span>
                </span>
                <textarea
                  rows={2}
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  className={estilos.areaTexto}
                />
              </label>

              <div className={estilos.acciones}>
                <Boton
                  variante="peligro"
                  onClick={() => void resolver(false)}
                  trabajando={decidir.isPending}
                  disabled={notas.trim().length < 10}
                >
                  Negar
                </Boton>

                <Boton
                  variante="principal"
                  onClick={() => void resolver(true)}
                  trabajando={decidir.isPending}
                >
                  Dar las horas
                </Boton>
              </div>
            </>
          ) : (
            <Aviso tipo="info">Esperando la decisión del Gerente operativo.</Aviso>
          )}
        </div>
      )}

      {/* ── Terminar el trabajo ──────────────────────────────────────────
          Las dos puertas que faltaban en todo el proyecto: nada escribía
          'cerrada' ni 'finalizada', así que ningún trabajo podía terminar.
          Van juntas porque son el mismo momento visto dos veces: se cierra y
          sale el acta, y días después el cliente la devuelve firmada. */}
      {enPrecierre && !pidioMasTiempo && puedeDecidir && (
        <div className={estilos.cierre}>
          {devolviendo ? (
            <>
              <label className={estilos.campo}>
                <span>¿Qué falta o qué hay que corregir?</span>
                <textarea
                  rows={3}
                  value={motivoDevolucion}
                  onChange={(e) => setMotivoDevolucion(e.target.value)}
                  placeholder="Faltó la foto del tablero cerrado, la canalización quedó a medias…"
                  autoFocus
                />
                <small>Es lo único que el técnico va a ver, así que sea concreto.</small>
              </label>

              <div className={estilos.acciones}>
                <Boton
                  variante="peligro"
                  onClick={() => void devolverAlTecnico()}
                  trabajando={devolver.isPending}
                  disabled={motivoDevolucion.trim().length < 10}
                >
                  Devolvérsela
                </Boton>
                <Boton
                  variante="secundario"
                  onClick={() => {
                    setDevolviendo(false)
                    setMotivoDevolucion('')
                  }}
                >
                  Cancelar
                </Boton>
              </div>
            </>
          ) : cerrando ? (
            <>
              <label className={estilos.campo}>
                <span>
                  Notas del cierre{' '}
                  {horasHechas === 0 && (
                    <span className={estilos.obligatorio}>obligatorias sin horas</span>
                  )}
                </span>
                <textarea
                  rows={2}
                  value={motivoCierre}
                  onChange={(e) => setMotivoCierre(e.target.value)}
                  className={estilos.areaTexto}
                  placeholder={
                    horasHechas === 0
                      ? 'Por ejemplo: el trabajo se hizo antes de usar la aplicación.'
                      : 'Opcional'
                  }
                  autoFocus
                />
              </label>

              <div className={estilos.acciones}>
                <Boton
                  variante="principal"
                  onClick={() => void terminar()}
                  trabajando={cerrar.isPending}
                  // Sin horas registradas la base exige explicación: se impide
                  // aquí para no mandar al servidor algo que ya se ve que falla.
                  disabled={horasHechas === 0 && motivoCierre.trim().length < 10}
                >
                  Confirmar el cierre
                </Boton>
                <Boton
                  variante="secundario"
                  onClick={() => {
                    setCerrando(false)
                    setMotivoCierre('')
                  }}
                >
                  Cancelar
                </Boton>
              </div>
            </>
          ) : (
            <>
              {horasHechas === 0 && (
                <Aviso tipo="alerta" titulo="Sin horas registradas">
                  Nadie marcó llegada ni salida en esta orden. Se puede cerrar igual, pero hay que
                  decir por qué: queda en la bitácora.
                </Aviso>
              )}

              <div className={estilos.acciones}>
                {/* CERRAR NO PIDE NOTAS. Cerrar es estar de acuerdo con lo que
                    el técnico reportó; obligar a escribir algo cada vez solo
                    produce «ok» y «listo», que no le sirven a nadie. La única
                    excepción es una orden sin horas: ahí la base exige la
                    explicación y por eso se abre el paso intermedio. */}
                <Boton
                  variante="principal"
                  onClick={() => (horasHechas === 0 ? setCerrando(true) : void terminar())}
                  trabajando={cerrar.isPending}
                >
                  Cerrar el trabajo
                </Boton>

                {/* LA TERCERA SALIDA. Antes solo se podía cerrar o dejar la
                    orden quieta, y dejarla quieta no le avisa nada al técnico. */}
                <Boton variante="secundario" onClick={() => setDevolviendo(true)}>
                  Devolver al técnico
                </Boton>
              </div>
            </>
          )}
        </div>
      )}

      {trabajo.estado === 'cerrada' && puedeDecidir && (
        <div className={estilos.cierre}>
          {trabajo.acta_envio_error ? (
            <Aviso tipo="alerta" titulo="No se pudo enviar el acta">
              {trabajo.acta_envio_error} Corrige el WhatsApp del cliente y se reintenta, o vuelve a
              enviarla desde el chat.
            </Aviso>
          ) : (
            <Aviso tipo="info" titulo="El acta salió al cliente">
              {trabajo.acta_wa_enviada_en
                ? `Salió el ${fechaHora(trabajo.acta_wa_enviada_en)}. `
                : 'Está saliendo al WhatsApp del cliente. '}
              Cuando la devuelva firmada se registra aquí, y con la herramienta de vuelta el trabajo
              queda finalizado.
            </Aviso>
          )}

          <div className={estilos.acciones}>
            <Boton
              variante="principal"
              onClick={() => void registrarFirma()}
              trabajando={firmar.isPending}
            >
              El cliente firmó el acta
            </Boton>
          </div>
        </div>
      )}

      {/* ── Qué pasa con la herramienta ──────────────────────────────────
          La pregunta que no se hacía en ninguna pantalla. Aparece desde el
          precierre —cuando el técnico ya terminó y hay que decidir— y sigue
          mientras la orden esté cerrada, porque es justamente la devolución
          pendiente lo que impide que llegue a 'finalizada'. */}
      {/* Lo que sobró del material (0310), y lo que lleva de sobrantes (0314). */}
      <SobrantesDeLaOrden
        ordenTrabajoId={trabajo.id}
        terminada={enPrecierre || trabajo.estado === 'cerrada' || trabajo.estado === 'finalizada'}
      />

      {(enPrecierre || trabajo.estado === 'cerrada') && (
        <DestinoDeLasHerramientas
          ordenTrabajoId={trabajo.id}
          puedeRecibirEnBodega={puede('herramientas', 'editar')}
        />
      )}

      {trabajo.notas_precierre && !pidioMasTiempo && (
        <p className={estilos.decision}>Última decisión: {trabajo.notas_precierre}</p>
      )}
    </Tarjeta>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function Cifra({ rotulo, valor }: { rotulo: string; valor: number | null }) {
  return (
    <div className={estilos.cifra}>
      <span className={estilos.cifraValor}>{valor === null ? '—' : Number(valor)}</span>
      <span className={estilos.cifraRotulo}>{rotulo}</span>
    </div>
  )
}
